// utils/parseContact.js

import React from "react";

export const parseContact = (c = {}) => {
  const city = c?.city?.trim?.();
  const country = c?.country?.trim?.();

  let location = "N/A";
  if (city && country) {
    location = `${city}, ${country}`;
  } else if (city) {
    location = city;
  } else if (country) {
    location = country;
  }

  return {
    id: c?.enrich_id ||c?._id || c?.id,
    name: c?.name || `${c?.first_name || ""} ${c?.last_name || ""}`.trim(),
    title: c?.title || "",
    location,
    email:
      c?.email &&
      c?.email !== "email_not_unlocked@domain.com" &&
      c?.email !== ""
        ? c?.email
        : null,
    linkedin_url: c?.linkedin_url || "",
    caseType: c?.caseType,
    city,
    country,
    headline: c?.headline || "",
    db_id: c?._id,
    _id:c?._id,
    enrich_id:c?.enrich_id || c?.id
  };
};

export function normalizeProducts(productsInput) {
  if (Array.isArray(productsInput)) {
    return productsInput;
  }

  if (typeof productsInput === "string") {
    return productsInput
      .split(/[,|;]/) // Split by comma, pipe or semicolon
      .map((p) => p.trim()) // Remove whitespace
      .filter((p) => p.length); // Remove empty strings
  }

  if (productsInput && typeof productsInput === "object") {
    return [productsInput];
  }

  if (typeof productsInput === "number" || typeof productsInput === "boolean") {
    return [productsInput.toString()];
  }

  return [];
}

export function templatesReady(resp) {
  const isComplete = resp?.every(
    (item) =>
      Array.isArray(item.templates) &&
      item.templates.length >= 1 &&
      item.templates.every(
        (tpl) =>
          tpl?.subject !== "Generating..." &&
          tpl?.bodycontent !== "Generating..."
      )
  );

  return isComplete;
}


export function sameTemplates(a = [], b = []) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const A = a[i],
        B = b[i];
      if (
        A?._id !== B?._id ||
        A?.subject !== B?.subject ||
        A?.bodycontent !== B?.bodycontent
      ) {
        return false;
      }
    }
    return true;
  }


  export const mapToOption = (arr) => arr.map((val) => ({ label: val, value: val }));


  /**
 * Parse a string into an array of cleaned values.
 * Automatically splits by commas, semicolons, pipes, newlines, or spaces.
 *
 * @param {string} input - The input string to parse
 * @param {string|string[]} [customSeparator] - Optional custom separator(s)
 * @returns {string[]} - Array of trimmed non-empty values
 */
export function parseStringToArray(input, customSeparator) {
  if (typeof input !== "string" || !input.trim()) return [];

  let separators = customSeparator;

  // Default separators
  if (!customSeparator) {
    separators = [",", ";", "|", "\n", "\t"];
  } else if (typeof customSeparator === "string") {
    separators = [customSeparator];
  }

  // Build regex dynamically for multiple separators
  const regex = new RegExp(`[${separators.map(s => `\\${s}`).join("")}\\s]+`, "g");

  return input
    .split(regex)
    .map(item => item.trim())
    .filter(item => item.length > 0);
}


export const parseCSV = (val) => {
    if (!val) return [];
    return val?.split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  };



export function formatShortNumber(num, digits = 1) {
  if (num === null || num === undefined || isNaN(num)) return '0';
  const units = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' },
  ];

  const item = units
    .slice()
    .reverse()
    .find(unit => num >= unit.value);

  if (!item) return '0';
  const formatted = (num / item.value).toFixed(digits);
  // Remove trailing zeros like 1.0K → 1K
  return formatted.replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') + item.symbol;
}

export function deepFlatten(data) {
  const result = [];

  function helper(item) {
    if (Array.isArray(item)) {
      item.forEach(helper);
    } else {
      result.push(item);
    }
  }

  helper(data);
  return result;
}

function extractFromDOM(node) {
  let output = "";

  node.childNodes.forEach((child) => {
    // TEXT NODE
    if (child.nodeType === Node.TEXT_NODE) {
      output += child.textContent;
    }

    // ELEMENT NODE
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();

      if (tag === "br") {
        output += "\n";
      } 
      else if (["p", "div", "section"].includes(tag)) {
        output += "\n" + extractFromDOM(child) + "\n";
      }
      else if (tag === "li") {
        output += "• " + extractFromDOM(child) + "\n";
      }
      else if (["ul", "ol"].includes(tag)) {
        output += "\n" + extractFromDOM(child) + "\n";
      }
      else if (tag === "table") {
        output += "\n" + extractFromDOM(child) + "\n";
      }
      else if (tag === "tr") {
        output += extractFromDOM(child) + "\n";
      }
      else if (tag === "td" || tag === "th") {
        output += extractFromDOM(child) + "\t";
      }
      else {
        output += extractFromDOM(child);
      }
    }
  });

  // Clean spaces
  return output.replace(/[ \t]+/g, " ");
}


export function extractTextContentFormatted(node) {
  if (!node) return "";

  // 1️⃣ If node is a real DOM Node (HTMLElement)
  if (node instanceof HTMLElement || node instanceof Node) {
    return extractFromDOM(node)
      .replace(/\n{3,}/g, "\n\n")   // collapse big gaps
      .trim();
  }

  // 2️⃣ String or number
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  // 3️⃣ React Element (rare for this case)
  if (React.isValidElement(node)) {
    return extractTextContentFormatted(node.props.children);
  }

  // 4️⃣ Array
  if (Array.isArray(node)) {
    return node.map(extractTextContentFormatted).join("\n");
  }

  return "";
}



// utils/normalizeMappedProducts.js
export function normalizeMappedProducts(mapped_products) {
  if (!Array.isArray(mapped_products)) return [];

  const NAME_SYNONYMS = [
    "name",
    "title",
    "label",
    "productname",
    "product_name",
    "producttitle",
    "product_title",
    "displayname",
    "display_name",
    "product"
  ];

  return mapped_products
    .map((prod) => {
      if (typeof prod === "string") return prod;

      if (prod && typeof prod === "object") {
        //  Try direct synonym match first
        const key = Object.keys(prod||{})?.find((k) =>
          NAME_SYNONYMS.some((syn) => k.toLowerCase().includes(syn))
        );

        if (key && prod[key]) return String(prod[key]).trim();


        const firstStringValue = Object.values(prod).find(
          (v) => typeof v === "string" && v.trim().length > 0
        );
        return firstStringValue || "";
      }

      return "";
    }).filter((item) => Boolean(item))
}


export const STEP_IDS = {
  CAMPAIGN_INIT: "CAMPAIGN_INIT",
  CONTACT: "CONTACT",
  EMAIL_REACHOUT: "EMAIL_REACHOUT",
  FINAL_REVIEW: "FINAL_REVIEW",
};


export function formatNumberInternational(num) {
  if (!num) return "-";
  let number = num;

  if (typeof num === "string") {
    const lower = num.toLowerCase();
    if (lower?.includes("m")) number = parseFloat(lower) * 1_000_000;
    else if (lower?.includes("k")) number = parseFloat(lower) * 1_000;
  }

  if (isNaN(number)) return num;
  return number?.toLocaleString("en-US");
}


export const pxToRem = (px, base = 16) => `${px / base}rem`;



export const userAuthorized = ["agney@marketsandmarkets.com","sapnadip.baidya@marketsandmarkets.com","	manish.rane@marketsandmarkets.com","khwab.sheth@marketsandmarkets.com","shanaya.zafar@marketsandmarkets.com","prashant.kokate@marketsandmarkets.com","sonali.nanda@marketsandmarkets.com","ashwin.tapsale@marketsandmarkets.com"]
